import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, Plus, Edit2, Trash2, Clock, MapPin, Phone } from 'lucide-react';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FeedingVolunteers = () => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [formData, setFormData] = useState({
    location: '',
    time: '',
    volunteerName: '',
    volunteerContact: '',
    notes: '',
  });

  useEffect(() => {
    fetchSchedules();
  }, []);

  const fetchSchedules = async () => {
    try {
      const response = await axios.get(`${API}/feeding-schedules`);
      setSchedules(response.data.sort((a, b) => a.time.localeCompare(b.time)));
    } catch (error) {
      console.error('Error fetching schedules:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSchedule) {
        await axios.put(`${API}/feeding-schedules/${editingSchedule}`, formData);
      } else {
        await axios.post(`${API}/feeding-schedules`, formData);
      }
      fetchSchedules();
      resetForm();
    } catch (error) {
      console.error('Error saving schedule:', error);
      alert('Failed to save schedule. Please try again.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this feeding schedule?')) {
      try {
        await axios.delete(`${API}/feeding-schedules/${id}`);
        fetchSchedules();
      } catch (error) {
        console.error('Error deleting schedule:', error);
      }
    }
  };

  const startEdit = (schedule) => {
    setEditingSchedule(schedule.id);
    setFormData({
      location: schedule.location,
      time: schedule.time,
      volunteerName: schedule.volunteerName,
      volunteerContact: schedule.volunteerContact,
      notes: schedule.notes || '',
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingSchedule(null);
    setFormData({
      location: '',
      time: '',
      volunteerName: '',
      volunteerContact: '',
      notes: '',
    });
  };

  return (
    <div className="min-h-screen">
      <div className="bg-gradient-to-br from-green-400 to-emerald-500 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex justify-between items-center">
              <div>
                <div className="flex items-center gap-4 mb-4">
                  <div className="bg-white p-3 rounded-xl shadow-lg">
                    <Users className="w-8 h-8 text-green-600" />
                  </div>
                  <h1 className="text-4xl md:text-5xl font-extrabold text-white font-heading" data-testid="feeding-title">
                    Feeding & Volunteer Management
                  </h1>
                </div>
                <p className="text-lg text-white/90 font-medium">Coordinate feeding schedules and volunteer activities</p>
              </div>
              {!showForm && (
                <button
                  onClick={() => setShowForm(true)}
                  data-testid="add-schedule-button"
                  className="bg-white text-green-600 px-6 py-3 rounded-full font-bold shadow-xl hover:shadow-2xl transition-all hover:scale-105"
                >
                  <Plus className="w-5 h-5 inline mr-2" />
                  Add Schedule
                </button>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {showForm && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-lg border border-orange-100 p-8 mb-8"
          >
            <h2 className="text-2xl font-bold text-text-primary mb-6 font-heading">
              {editingSchedule ? 'Edit Feeding Schedule' : 'Add New Feeding Schedule'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Location *</label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    required
                    data-testid="input-location"
                    placeholder="e.g., Near Gate 2, Garden Area"
                    className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none"
                  />
                </div>
                <div>
                  <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Time *</label>
                  <input
                    type="time"
                    name="time"
                    value={formData.time}
                    onChange={handleChange}
                    required
                    data-testid="input-time"
                    className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none"
                  />
                </div>
                <div>
                  <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Volunteer Name *</label>
                  <input
                    type="text"
                    name="volunteerName"
                    value={formData.volunteerName}
                    onChange={handleChange}
                    required
                    data-testid="input-volunteer-name"
                    placeholder="Full name"
                    className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none"
                  />
                </div>
                <div>
                  <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Volunteer Contact *</label>
                  <input
                    type="tel"
                    name="volunteerContact"
                    value={formData.volunteerContact}
                    onChange={handleChange}
                    required
                    data-testid="input-volunteer-contact"
                    placeholder="+91 98765 43210"
                    className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  data-testid="input-notes"
                  rows="4"
                  placeholder="Any special instructions or notes..."
                  className="w-full px-4 py-3 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none resize-none"
                />
              </div>
              <div className="flex gap-4">
                <button
                  type="submit"
                  data-testid="save-schedule-button"
                  className="flex-1 bg-green-600 text-white shadow-lg shadow-green-500/30 hover:shadow-green-500/50 hover:-translate-y-0.5 transition-all duration-300 font-bold px-8 py-3 rounded-full"
                >
                  {editingSchedule ? 'Update Schedule' : 'Add Schedule'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  data-testid="cancel-button"
                  className="bg-white text-primary border-2 border-orange-100 hover:border-primary hover:bg-orange-50 transition-all duration-300 font-bold px-8 py-3 rounded-full"
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        )}

        {loading ? (
          <div className="text-center py-12" data-testid="loading-indicator">
            <div className="text-xl text-text-secondary font-medium">Loading schedules...</div>
          </div>
        ) : schedules.length === 0 ? (
          <div className="text-center py-12" data-testid="no-schedules">
            <Users className="w-16 h-16 text-text-muted mx-auto mb-4" />
            <div className="text-xl text-text-secondary font-medium mb-2">No feeding schedules yet</div>
            <p className="text-text-muted">Add schedules to coordinate feeding activities with volunteers</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {schedules.map((schedule, index) => (
              <motion.div
                key={schedule.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                data-testid={`schedule-card-${schedule.id}`}
                className="bg-white rounded-2xl shadow-sm border border-orange-100 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all"
              >
                <div className="bg-gradient-to-br from-green-400 to-emerald-500 p-6">
                  <div className="flex items-center gap-3 text-white mb-3">
                    <Clock className="w-6 h-6" />
                    <span className="text-2xl font-extrabold" data-testid="schedule-time">{schedule.time}</span>
                  </div>
                  <div className="flex items-center gap-2 text-white/90">
                    <MapPin className="w-4 h-4" />
                    <span className="font-medium" data-testid="schedule-location">{schedule.location}</span>
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <div className="text-xs font-bold text-text-muted mb-1">VOLUNTEER</div>
                    <div className="text-lg font-bold text-text-primary" data-testid="schedule-volunteer-name">{schedule.volunteerName}</div>
                  </div>
                  <a
                    href={`tel:${schedule.volunteerContact}`}
                    data-testid="schedule-volunteer-contact"
                    className="flex items-center gap-2 p-3 rounded-xl bg-green-50 hover:bg-green-100 transition-all"
                  >
                    <Phone className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-bold text-green-700">{schedule.volunteerContact}</span>
                  </a>
                  {schedule.notes && (
                    <p className="text-sm text-text-secondary border-t border-orange-100 pt-3" data-testid="schedule-notes">
                      {schedule.notes}
                    </p>
                  )}
                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={() => startEdit(schedule)}
                      data-testid="edit-schedule-button"
                      className="flex-1 px-4 py-2 rounded-xl bg-blue-50 text-blue-700 hover:bg-blue-100 transition-all font-bold flex items-center justify-center gap-2"
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(schedule.id)}
                      data-testid="delete-schedule-button"
                      className="flex-1 px-4 py-2 rounded-xl bg-red-50 text-red-700 hover:bg-red-100 transition-all font-bold flex items-center justify-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border-2 border-orange-200 p-8 mt-8">
          <h3 className="text-xl font-bold text-text-primary mb-4 font-heading">Feeding Guidelines</h3>
          <ul className="space-y-2 text-text-secondary">
            <li className="flex items-start gap-2">
              <span className="text-orange-600 font-bold">•</span>
              <span>Use designated feeding areas only</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-orange-600 font-bold">•</span>
              <span>Maintain consistent feeding times for animal welfare</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-orange-600 font-bold">•</span>
              <span>Clean up the area immediately after feeding</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-orange-600 font-bold">•</span>
              <span>Coordinate with other volunteers to avoid over-feeding</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-orange-600 font-bold">•</span>
              <span>Report any sick or injured animals immediately</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default FeedingVolunteers;